import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";

const LOGO_DEFAULT = "https://customer-assets.emergentagent.com/job_elite-realty-40/artifacts/q2vcqxyz_%D8%AA%D8%B5%D9%85%D9%8A%D9%85%20%D8%A8%D8%AF%D9%88%D9%86%20%D8%B9%D9%86%D9%88%D8%A7%D9%86%20-%202026-06-21T092601.962.png";

const navLinks = [
  { label: "الرئيسية", href: "/#home" },
  { label: "خدماتنا", href: "/#services" },
  { label: "عن الشركة", href: "/#values" },
  { label: "العروض العقارية", href: "/#projects" },
  { label: "مخططات جازان", href: "/plots" },
  { label: "تواصل معنا", href: "/#contact" },
];

const Navbar = ({ contact, logo }) => {
  const LOGO = logo || LOGO_DEFAULT;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    // Trigger entrance animation
    const t = setTimeout(() => setLoaded(true), 100);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t);
    };
  }, []);

  const whatsapp = contact?.whatsapp || "966555661937";

  return (
    <>
      <nav
        data-testid="navbar"
        className="fixed top-0 right-0 left-0 z-50 transition-all duration-700"
        style={{
          background: scrolled
            ? "rgba(23,50,105,0.97)"
            : "linear-gradient(180deg, rgba(23,50,105,0.92) 0%, rgba(23,50,105,0.5) 70%, rgba(23,50,105,0.0) 100%)",
          backdropFilter: scrolled ? "blur(12px)" : "blur(4px)",
          boxShadow: scrolled ? "0 4px 32px rgba(23,50,105,0.4)" : "none",
          borderBottom: scrolled ? "1px solid rgba(236,199,117,0.2)" : "none",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">

            {/* Logo - RIGHT (first in RTL flex) */}
            <a
              href="#home"
              className={`flex-shrink-0 transition-all duration-700 ${
                loaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-6"
              }`}
              style={{ transitionDelay: "0.05s" }}
            >
              <img
                src={LOGO}
                alt="الوسيم العقارية"
                className="h-14 w-auto object-contain drop-shadow-lg"
                style={{ mixBlendMode: "screen" }}
                data-testid="navbar-logo"
              />
            </a>

            {/* Nav Links - Center */}
            <div className="hidden lg:flex items-center gap-7">
              {navLinks.map((link, i) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={`nav-link-${link.label}`}
                  className={`nav-link-white text-white/85 font-semibold text-sm hover:text-[#ecc775] transition-all duration-300 pb-1 relative group ${
                    loaded ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
                  }`}
                  style={{ transitionDelay: `${0.15 + i * 0.08}s`, transition: "opacity 0.6s ease, transform 0.6s ease, color 0.3s ease" }}
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#ecc775] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-right"></span>
                </a>
              ))}
            </div>

            {/* CTA + Phone - LEFT (last in RTL flex) */}
            <div
              className={`hidden lg:flex items-center gap-4 transition-all duration-700 ${
                loaded ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
              }`}
              style={{ transitionDelay: "0.5s" }}
            >
              <a
                href={`tel:${contact?.phone1 || "0555661937"}`}
                className="text-white/80 font-semibold text-sm hover:text-[#ecc775] transition-colors"
                dir="ltr"
              >
                {contact?.phone1 || "0555661937"}
              </a>
              <a
                data-testid="nav-whatsapp-btn"
                href={`https://wa.me/${whatsapp}`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#ecc775] text-[#173269] px-5 py-2.5 font-black text-sm flex items-center gap-2 hover:bg-white transition-all duration-300 rounded-sm shadow-lg shadow-[#ecc775]/20"
              >
                <Phone size={15} />
                تواصل الآن
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden transition-all duration-300 overflow-hidden ${
            mobileOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
          style={{ background: "rgba(10,17,40,0.97)", backdropFilter: "blur(12px)" }}
        >
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-white/85 font-semibold text-base hover:text-[#ecc775] transition-colors py-3 border-b border-white/10"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </a>
            ))}
            <a
              href={`https://wa.me/${whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#ecc775] text-[#173269] px-5 py-3.5 font-black text-center rounded-sm mt-3"
              onClick={() => setMobileOpen(false)}
            >
              تواصل عبر واتساب
            </a>
          </div>
        </div>
      </nav>

      {/* Decorative diagonal divider that overlaps navbar → hero */}
      <div
        className="fixed top-[76px] right-0 left-0 z-40 pointer-events-none"
        style={{
          opacity: scrolled ? 0 : 1,
          transition: "opacity 0.5s ease",
        }}
      >
        <svg viewBox="0 0 1440 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 0L1440 0L1440 8L720 24L0 8Z" fill="rgba(236,199,117,0.12)" />
        </svg>
      </div>
    </>
  );
};

export default Navbar;
